// `avatar uninstall` — gỡ Avatar block khỏi <git-common-dir>/info/exclude của
// mọi repo trong manifest .claude/repos.json (ticket #138 phase-04). Đối ứng
// với phase-02 (ghi block vào đó): không có bước này, gỡ Avatar sẽ để lại rule
// trong file VÔ HÌNH (không hiện ở `git status`) vĩnh viễn.
//
// BACKUP TRƯỚC KHI SỬA (bất biến #129, không ngoại lệ): copy info/exclude sang
// backupDir rồi mới ghi; copy fail → KHÔNG sửa file.
import { copyFile, readFile } from "node:fs/promises";
import { basename, join } from "node:path";
import { stripAvatarBlockFromContent } from "./avatar-block-text-ops.js";
import { ensureDir, pathExists, writeTextAtomic } from "./filesystem-helpers.js";
import { AVATAR_MARKER_START } from "./gitignore-template-loader.js";
import { probeGitCli, resolveGitCommonDir } from "./resolve-git-common-dir.js";
import { readReposManifest } from "./workspace-repos-manifest-store.js";

export interface PruneExcludeResult {
  stripped: string[]; // đã gỡ block khỏi info/exclude (đã backup xong)
  noBlock: string[]; // exclude không có marker Avatar (idempotent — lần 2)
  noExcludeFile: string[]; // repo là git repo nhưng chưa có <gitdir>/info/exclude
  corrupted: string[]; // marker hỏng (START không END / END trước START) — KHÔNG đụng
  skippedNotGitRepo: string[]; // resolveGitCommonDir → null; CHỈ nghĩa "không phải git repo"
  missing: string[]; // repo trong manifest nhưng thiếu folder
  // GitOutputUnexpectedError (git < 2.31 lọt qua probe, rev-parse trả rc=0 kèm
  // rác — ĐO ĐƯỢC, plan.md §Phép đo D6) rơi vào ĐÂY, KHÔNG map sang
  // skippedNotGitRepo (mislabel) và KHÔNG đẻ bucket mới.
  failed: { name: string; error: string }[];
  backups: { name: string; backupPath: string }[];
  // Outcome cấp-run: probeGitCli() chạy MỘT lần đầu hàm (B3). ≠ "ok" → trả
  // ngay, KHÔNG loop repo, KHÔNG xếp repo nào vào skippedNotGitRepo.
  gitOutcome: "ok" | "gitUnavailable" | "gitTooOld";
}

function defaultBackupDir(workspaceRoot: string): string {
  return join(workspaceRoot, ".claude", "_backup");
}

// try/catch PER-REPO: 1 repo lỗi KHÔNG chặn repo còn lại + KHÔNG throw ra ngoài
// — `uninstall` không bao giờ được abort vì bước này (§Non-functional).
export async function pruneAvatarBlockFromRepoExcludes(
  workspaceRoot: string,
  opts?: { backupDir?: string },
): Promise<PruneExcludeResult> {
  const stripped: string[] = [];
  const noBlock: string[] = [];
  const noExcludeFile: string[] = [];
  const corrupted: string[] = [];
  const skippedNotGitRepo: string[] = [];
  const missing: string[] = [];
  const failed: { name: string; error: string }[] = [];
  const backups: { name: string; backupPath: string }[] = [];

  // B3 — probeGitCli() MỘT lần cho cả run, TRƯỚC KHI LOOP. ≠ "ok" → trả ngay.
  // Máy thiếu/quá cũ git phải in "git không chạy được"; in "Bỏ qua (không phải
  // git repo)" cho cả 4 repo là SAI SỰ THẬT — và ở uninstall hậu quả nặng hơn
  // sync: block sót nằm trong file VÔ HÌNH, .gitignore thì đã bị self-heal gỡ
  // ⇒ user không còn dấu vết nào để lần ra.
  const probe = probeGitCli();
  if (probe.status !== "ok") {
    return {
      stripped,
      noBlock,
      noExcludeFile,
      corrupted,
      skippedNotGitRepo,
      missing,
      failed,
      backups,
      gitOutcome: probe.status === "unavailable" ? "gitUnavailable" : "gitTooOld",
    };
  }

  const repos = await readReposManifest(workspaceRoot);
  const backupDir = opts?.backupDir ?? defaultBackupDir(workspaceRoot);

  for (const r of repos) {
    const repoPath = join(workspaceRoot, r.path);
    if (!(await pathExists(repoPath))) {
      missing.push(r.name);
      continue;
    }

    try {
      // Chỉ gọi sau khi probe "ok" (phase-01 F3b) ⇒ `null` giờ mang ĐÚNG MỘT
      // nghĩa: repoPath không phải git repo.
      const common = resolveGitCommonDir(repoPath);
      if (common === null) {
        skippedNotGitRepo.push(r.name);
        continue;
      }

      const excludePath = join(common, "info", "exclude");
      if (!(await pathExists(excludePath))) {
        noExcludeFile.push(r.name);
        continue;
      }

      const existing = await readFile(excludePath, "utf8");
      const { content, status } = stripAvatarBlockFromContent(existing);

      if (status === "no-marker") {
        noBlock.push(r.name);
        continue;
      }
      if (status === "corrupted") {
        corrupted.push(r.name);
        continue;
      }
      if (content === null) {
        // Không nên xảy ra (status "stripped" luôn kèm content non-null) — giữ
        // fail-soft nếu hợp đồng avatar-block-text-ops.ts đổi sau này.
        failed.push({
          name: r.name,
          error: "stripAvatarBlockFromContent trả content=null cho status=stripped (bất thường)",
        });
        continue;
      }

      // BACKUP TRƯỚC KHI SỬA (bất biến, không ngoại lệ). Tên file mang tiền tố
      // `exclude-` — 2 pruner (đây + strip .gitignore phase-03) ghi CHUNG một
      // thư mục repo-ignores/, khác tiền tố mới không ghi đè nhau (N3).
      await ensureDir(backupDir);
      const ts = new Date().toISOString().replace(/[:.]/g, "-");
      const backupPath = join(backupDir, `exclude-${basename(r.name)}-${ts}`);
      await copyFile(excludePath, backupPath);

      // GHI FILE RỖNG, KHÔNG XOÁ (khác .gitignore/D3): info/exclude là file của
      // git, git tự dùng lại được; xoá đi không có lợi ích tương ứng.
      await writeTextAtomic(excludePath, content);
      stripped.push(r.name);
      backups.push({ name: r.name, backupPath });
    } catch (err) {
      failed.push({ name: r.name, error: err instanceof Error ? err.message : String(err) });
    }
  }

  return {
    stripped,
    noBlock,
    noExcludeFile,
    corrupted,
    skippedNotGitRepo,
    missing,
    failed,
    backups,
    gitOutcome: "ok",
  };
}

// ĐỌC-THUẦN: không ghi, không xoá, không tạo backup dir. Dùng cho summary/
// --dry-run của `uninstall`.
//
// Trả OBJECT (không phải string[] trần — N4): `uncheckable = true` khi
// probeGitCli() ≠ "ok". Danh sách rỗng KHÔNG CÒN đồng nghĩa "không có gì để
// dọn" — thiếu cờ này thì --dry-run in ✓ giả, user yên tâm uninstall, block ở
// lại trong file VÔ HÌNH vĩnh viễn.
export async function findReposWithBlockInGitExclude(
  workspaceRoot: string,
): Promise<{ repos: string[]; uncheckable: boolean }> {
  const probe = probeGitCli();
  if (probe.status !== "ok") {
    return { repos: [], uncheckable: true };
  }

  const manifest = await readReposManifest(workspaceRoot);
  const found: string[] = [];
  for (const r of manifest) {
    const repoPath = join(workspaceRoot, r.path);
    if (!(await pathExists(repoPath))) continue;
    try {
      const common = resolveGitCommonDir(repoPath);
      if (common === null) continue;
      const excludePath = join(common, "info", "exclude");
      if (!(await pathExists(excludePath))) continue;
      const content = await readFile(excludePath, "utf8");
      if (content.includes(AVATAR_MARKER_START)) found.push(r.name);
    } catch {
      // Đọc lỗi / git output bất thường ở 1 repo → bỏ qua repo đó (đọc-thuần,
      // không có bucket `failed`). Pruner thật sẽ xếp nó vào `failed`.
    }
  }
  return { repos: found, uncheckable: false };
}
